import React from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "../../theme/tokens";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    CircularProgress,
} from "@mui/material";

/**
 * BudgetDimensionsTable - Displays calculated physical dimensions/specifications
 * NOT a financial summary - this shows measurements only.
 */
const BudgetDimensionsTable = ({ data, loading }) => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <Box
                sx={{
                    position: "relative",
                    minHeight: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                    p: 4,
                }}
            >
                <CircularProgress size={48} sx={{ color: "primary.main" }} />
                <Typography variant="body1" sx={{ color: "var(--text-secondary)", fontWeight: 500 }}>
                    {t("budgetForm.dimensionsTable.loading")}
                </Typography>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                    {t("budgetForm.dimensionsTable.noData")}
                </Typography>
            </Box>
        );
    }

    const nozzleRow = (nozzle) => {
        const parts = [];
        if (nozzle.qty) parts.push(`${t("budgetForm.dimensionsTable.fields.nozzleQty")}: ${nozzle.qty}`);
        if (nozzle.dn) parts.push(`DN ${nozzle.dn}`);
        if (nozzle.pn) parts.push(`PN ${nozzle.pn}`);
        return { label: nozzle.designation, value: parts.join("  ·  ") || null };
    };

    const groups = [
        {
            title: t("budgetForm.dimensionsTable.groups.projectInfo"),
            rows: [
                { label: t("budgetForm.dimensionsTable.fields.propose"), value: data.propose },
                { label: t("budgetForm.dimensionsTable.fields.projectCode"), value: data.projectCode },
                { label: t("budgetForm.dimensionsTable.fields.degassingCapacity"), value: data.degassingCapacity ? `${data.degassingCapacity} t/h` : null },
            ],
        },
        {
            title: t("budgetForm.dimensionsTable.groups.tankDimensions"),
            rows: [
                { label: t("budgetForm.dimensionsTable.fields.diameter"), value: data.tankDiameter ? `${data.tankDiameter} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.tankBodyLength"), value: data.tankBodyLength ? `${data.tankBodyLength} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.outerDiameter"), value: data.tankOuterDiameter ? `${data.tankOuterDiameter} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.shellThickness"), value: data.tankShellThickness ? `${data.tankShellThickness} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.headThickness"), value: data.tankHeadThickness ? `${data.tankHeadThickness} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.totalVolume"), value: data.tankTotalVolume ? `${data.tankTotalVolume} m³` : null },
                { label: t("budgetForm.dimensionsTable.fields.usefulVolume"), value: data.tankUsefulVolume ? `${data.tankUsefulVolume} m³` : null },
                { label: t("budgetForm.dimensionsTable.fields.material"), value: data.tankMaterial },
            ],
        },
        {
            title: t("budgetForm.dimensionsTable.groups.towerDimensions"),
            rows: [
                { label: t("budgetForm.dimensionsTable.fields.diameter"), value: data.towerDiameter ? `${data.towerDiameter} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.height"), value: data.towerHeight ? `${data.towerHeight} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.shellThickness"), value: data.towerShellThickness ? `${data.towerShellThickness} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.headThickness"), value: data.towerHeadThickness ? `${data.towerHeadThickness} mm` : null },
                { label: t("budgetForm.dimensionsTable.fields.numberOfSprayers"), value: data.numberOfSprayers },
                { label: t("budgetForm.dimensionsTable.fields.numberOfTrays"), value: data.numberOfTrays },
                { label: t("budgetForm.dimensionsTable.fields.material"), value: data.towerMaterial },
            ],
        },
        {
            title: t("budgetForm.dimensionsTable.groups.operatingConditions"),
            rows: [
                { label: t("budgetForm.dimensionsTable.fields.operatingPressure"), value: data.operatingPressure ? `${data.operatingPressure} barg` : null },
                { label: t("budgetForm.dimensionsTable.fields.operatingTemperature"), value: data.operatingTemperature ? `${data.operatingTemperature} °C` : null },
                { label: t("budgetForm.dimensionsTable.fields.availableSteamPressure"), value: data.availableSteamPressure ? `${data.availableSteamPressure} barg` : null },
                { label: t("budgetForm.dimensionsTable.fields.availableSteamTemperature"), value: data.availableSteamTemperature ? `${data.availableSteamTemperature} °C` : null },
            ],
        },
    ];

    if (data.vacuumBreakerVolume || data.vacuumBreakerAirFlow || data.vacuumBreakerValveSelection) {
        groups.push({
            title: t("budgetForm.dimensionsTable.groups.vacuumBreaker"),
            rows: [
                { label: t("budgetForm.dimensionsTable.fields.vacuumVolume"), value: data.vacuumBreakerVolume ? `${data.vacuumBreakerVolume} m³` : null },
                { label: t("budgetForm.dimensionsTable.fields.vacuumAirFlow"), value: data.vacuumBreakerAirFlow ? `${data.vacuumBreakerAirFlow} m³/h` : null },
                { label: t("budgetForm.dimensionsTable.fields.vacuumValveSelection"), value: data.vacuumBreakerValveSelection },
            ],
        });
    }

    if (data.nozzleList?.tankNozzles?.length > 0) {
        groups.push({
            title: t("budgetForm.dimensionsTable.groups.tankNozzleList"),
            rows: data.nozzleList.tankNozzles.map(nozzleRow),
        });
    }

    if (data.nozzleList?.towerNozzles?.length > 0) {
        groups.push({
            title: t("budgetForm.dimensionsTable.groups.towerNozzleList"),
            rows: data.nozzleList.towerNozzles.map(nozzleRow),
        });
    }

    return (
        <Box sx={{ p: { xs: 1, md: 3 } }}>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 500 }}>
                    <TableBody>
                        {groups.map((group, gIdx) => (
                            <React.Fragment key={gIdx}>
                                <TableRow sx={{ backgroundColor: "#9e9e9e" }}>
                                    <TableCell colSpan={2} sx={{ color: "#fff", fontWeight: 700, fontStyle: "italic" }}>
                                        {group.title}
                                    </TableCell>
                                </TableRow>
                                {group.rows.map((row, rIdx) => (
                                    <TableRow
                                        key={rIdx}
                                        sx={{ backgroundColor: rIdx % 2 === 0 ? "transparent" : alpha.primary04 }}
                                    >
                                        <TableCell sx={{ color: "var(--text-primary)", fontWeight: 500 }}>
                                            {row.label}
                                        </TableCell>
                                        <TableCell sx={{ color: "var(--text-secondary)" }}>
                                            {row.value ?? "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default BudgetDimensionsTable;